/*
 * Conferência de rede no cliente — change add-restricao-de-ip.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ QUEM DECIDE É A API. ESTA CONFERÊNCIA SÓ POUPA A IDA.                    │
 * │                                                                          │
 * │ `domain.NormalizarRede` é a regra: ela grava `/32` para endereço solto,  │
 * │ desmapeia `::ffff:a.b.c.d` e recusa `/0`. Aqui repetem-se só as recusas  │
 * │ que cabem sem ambiguidade — sintaxe, `/0` e bits de host —, com as       │
 * │ mesmas frases, para que o operador leia a mesma coisa nos dois lados.    │
 * │ Uma divergência entre as duas nunca libera nada: no pior caso, a API     │
 * │ recusa o que esta função deixou passar, e a recusa aparece no modal.     │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Bits de host são RECUSADOS, e não mascarados, pelo mesmo motivo da API:
 * `203.0.113.10/24` corrigido em silêncio trocaria "este servidor" por "a faixa
 * inteira dele" sem ninguém decidir.
 */

export type ConferenciaDeRede = { ok: true } | { ok: false; erro: string };

const DECIMAL_SEM_ZERO_A_ESQUERDA = /^(0|[1-9]\d*)$/;

/** Os quatro octetos, ou `null`. Zero à esquerda é recusado, como no `netip`. */
function octetosIPv4(texto: string): [number, number, number, number] | null {
  const partes = texto.split(".");
  if (partes.length !== 4) return null;
  if (!partes.every((parte) => DECIMAL_SEM_ZERO_A_ESQUERDA.test(parte) && Number(parte) <= 255)) {
    return null;
  }
  const [a, b, c, d] = partes.map(Number) as [number, number, number, number];
  return [a, b, c, d];
}

/** Os oito grupos de 16 bits, ou `null`. Aceita `::` e IPv4 embutido no fim. */
function gruposIPv6(texto: string): number[] | null {
  if (texto.includes("%")) return null; // zona só faz sentido dentro de uma máquina

  const metades = texto.split("::");
  if (metades.length > 2) return null;

  function lerLado(lado: string, ultimo: boolean): number[] | null {
    if (lado === "") return [];
    const pedacos = lado.split(":");
    const grupos: number[] = [];

    for (const [indice, pedaco] of pedacos.entries()) {
      if (ultimo && indice === pedacos.length - 1 && pedaco.includes(".")) {
        const octetos = octetosIPv4(pedaco);
        if (octetos === null) return null;
        const [a, b, c, d] = octetos;
        grupos.push((a << 8) | b, (c << 8) | d);
        continue;
      }
      if (!/^[0-9a-fA-F]{1,4}$/.test(pedaco)) return null;
      grupos.push(parseInt(pedaco, 16));
    }
    return grupos;
  }

  if (metades.length === 1) {
    const grupos = lerLado(texto, true);
    return grupos !== null && grupos.length === 8 ? grupos : null;
  }

  const [antes = "", depois = ""] = metades;
  const esquerda = lerLado(antes, false);
  const direita = lerLado(depois, true);
  if (esquerda === null || direita === null) return null;

  // `::` vale por pelo menos um grupo zerado.
  const faltam = 8 - esquerda.length - direita.length;
  if (faltam < 1) return null;
  return [...esquerda, ...new Array<number>(faltam).fill(0), ...direita];
}

/** IPv6 na forma comprimida: a maior sequência de dois ou mais zeros vira `::`. */
function escreverIPv6(grupos: number[]): string {
  let inicio = -1;
  let tamanho = 0;
  for (let i = 0; i < 8; ) {
    if (grupos[i] !== 0) {
      i++;
      continue;
    }
    let fim = i;
    while (fim < 8 && grupos[fim] === 0) fim++;
    if (fim - i > tamanho) {
      inicio = i;
      tamanho = fim - i;
    }
    i = fim;
  }

  const hex = grupos.map((grupo) => grupo.toString(16));
  if (tamanho < 2) return hex.join(":");
  return `${hex.slice(0, inicio).join(":")}::${hex.slice(inicio + tamanho).join(":")}`;
}

/** Zera os bits à direita do prefixo, sobre palavras de `largura` bits. */
function mascarar(palavras: number[], largura: number, bits: number): number[] {
  return palavras.map((palavra, indice) => {
    const restam = bits - indice * largura;
    if (restam >= largura) return palavra;
    if (restam <= 0) return 0;
    const mascara = ((1 << largura) - 1) ^ ((1 << (largura - restam)) - 1);
    return palavra & mascara;
  });
}

export function conferirRede(bruto: string): ConferenciaDeRede {
  const texto = bruto.trim();
  if (texto === "") {
    return { ok: false, erro: "Informe um endereço IP ou uma faixa em CIDR." };
  }

  const barra = texto.indexOf("/");
  const endereco = barra === -1 ? texto : texto.slice(0, barra);
  const prefixo = barra === -1 ? null : texto.slice(barra + 1);

  const v4 = octetosIPv4(endereco);
  const palavras = v4 ?? gruposIPv6(endereco);
  if (palavras === null) {
    return {
      ok: false,
      erro:
        prefixo === null
          ? `"${texto}" não é um endereço IP válido.`
          : `"${texto}" não é uma faixa CIDR válida.`,
    };
  }

  // Endereço solto vira rede de um endereço só, e não há o que conferir.
  if (prefixo === null) return { ok: true };

  const largura = v4 !== null ? 32 : 128;
  if (!DECIMAL_SEM_ZERO_A_ESQUERDA.test(prefixo) || Number(prefixo) > largura) {
    return { ok: false, erro: `"${texto}" não é uma faixa CIDR válida.` };
  }

  const bits = Number(prefixo);
  if (bits === 0) {
    return {
      ok: false,
      erro: "Essa faixa libera qualquer origem. Para não restringir, deixe a lista vazia.",
    };
  }

  const mascaradas = mascarar(palavras, v4 !== null ? 8 : 16, bits);
  if (mascaradas.every((palavra, indice) => palavra === palavras[indice])) return { ok: true };

  const escrever = (lista: number[]) => (v4 !== null ? lista.join(".") : escreverIPv6(lista));
  return {
    ok: false,
    erro: `${escrever(palavras)}/${bits} tem bits de host. Use ${escrever(mascaradas)}/${bits} para a faixa inteira ou ${escrever(palavras)}/${largura} para um endereço só.`,
  };
}
